import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { CoreProvider } from '../core.provider';
import { Mutex } from '../auth/mutex';

export const updateLocalFromCloud = <T>(local: T, cloud: any): T => {
  for (const key in cloud) {
    local[key as keyof T] = cloud[key];
  }
  return local;
}

export type UserProfileLocalConfiguration = {
  selectedCustomEliteConfiguration?: string;
}

@Injectable({
  providedIn: 'root'
})
export class StorageService {

  private core?: CoreProvider;
  private storageAvailable = false;

  constructor(private readonly storage: Storage) { }

  async initCore(core: CoreProvider) {
    this.core = core;
    await this.storage.create();
    this.storageAvailable = true;

    console.log('Storage initialized with driver:', this.storage.driver);
  }

  setItem(key: string, value: any) {
    if (!this.storageAvailable) throw new Error('Storage not available');
    return this.storage.set(key, value);
  }

  getItem(key: string) {
    if (!this.storageAvailable) throw new Error('Storage not available');
    return this.storage.get(key);
  }

  removeItem(key: string) {
    if (!this.storageAvailable) throw new Error('Storage not available');
    return this.storage.remove(key);
  }

  async reset() {
    if (!this.storageAvailable) throw new Error('Storage not available');
    await this.storage.clear();
    await this.storage.create();
  }

  //#region User Profile
  private readonly USER_PROFILE_STORAGE_KEY = 'user-profile';

  async getUserProfile(): Promise<UserProfileLocalConfiguration> {
    return await this.getItem(this.USER_PROFILE_STORAGE_KEY) as UserProfileLocalConfiguration;
  }

  async setUserProfile(userProfile: UserProfileLocalConfiguration) {
    return await this.setItem(this.USER_PROFILE_STORAGE_KEY, userProfile);
  }
  //#endregion


  async syncToCloud<
    T extends { localId: string, uuid?: string, pendingDelete?: boolean, localLastUpdate: number|null },
    Y extends { uuid?: string, localId: string, updatedAt?: string | null }
  >(
    localItems: T[],
    cloudCrud: {
      getall: () => Promise<Y[]>;
      create: (item: T) => Promise<Y>;
      update: (item: T) => Promise<Y>;
      delete: (item: T) => Promise<void>;
    },
    parseCloudItemFn: (cloudItem: Y, localSentItem?: T) => T = (item: Y) => item as unknown as T,
    extraChecksFn: {
      create?: (localItem: T) => boolean;
      update?: (localItem: T) => boolean;
      delete?: (localItem: T) => boolean;
    } = {}
  ): Promise<T[]> {
    let syncErrors: unknown[] = [];
    if (!this.core?.auth.isAuthenticated) throw new Error('Not authenticated, cannot sync to cloud');
    if (!this.core?.api.connectedToCloud$.value) throw new Error('Not connected to cloud, cannot sync to cloud');

    console.log('Syncing to cloud', localItems);

    const cloudItems = await cloudCrud.getall();
    const cloudItemsMap = new Map(cloudItems.map(cloudItem => [cloudItem.uuid, cloudItem]));
    const cloudItemsLocalIdMap = new Map(cloudItems.map(cloudItem => [cloudItem.localId, cloudItem]));

    // Remove local items that are not in the cloud and has an uuid locally
    localItems = localItems.filter(localItem => {
      if (!localItem.uuid) return true;
      return cloudItemsMap.has(localItem.uuid);
    });

    // Generate lists of items to create, delete and update
    const itemsToCreate = localItems.filter(localItem => !localItem.uuid).filter(localItem => extraChecksFn.create?.(localItem) ?? true);
    const itemsToDelete = localItems.filter(localItem => localItem.uuid && localItem.pendingDelete).filter(localItem => extraChecksFn.update?.(localItem) ?? true);
    const itemsToUpdate = localItems.filter(localItem => {
      // For faster processing, we only will try to fetch related cloud item if local item has a uuid and a localLastUpdate
      if (!localItem.uuid || !localItem.localLastUpdate) return false;
      const cloudItem = cloudItemsMap.get(localItem.uuid);
      if (!cloudItem) {
        console.error(`Item ${localItem.localId} has a uuid but no related cloud item, maybe deleted from another device`);
        return false; // This should never happen
      }
      return cloudItem.updatedAt && localItem.localLastUpdate > new Date(cloudItem.updatedAt).getTime();
    }).filter(localItem => extraChecksFn.delete?.(localItem) ?? true);

    // If local item is not on any list to update to cloud, ensure to update from cloud
    const itemsUuidsToPreventCloudUpdate = new Set([...itemsToUpdate.map(item => item.uuid), ...itemsToDelete.map(item => item.uuid)]);
    for (const localItem of localItems) {
      try {
        // If local item has a uuid and is not in the list to prevent cloud update, or if local item has no uuid, update from cloud
        if ((localItem.uuid && !itemsUuidsToPreventCloudUpdate.has(localItem.uuid)) || !localItem.uuid) {
          if (cloudItemsLocalIdMap.has(localItem.localId)) {
            updateLocalFromCloud(localItem, parseCloudItemFn(cloudItemsLocalIdMap.get(localItem.localId)!, localItem));
          }
        }
      } catch (error) {
        console.error('Error updating local item from cloud, continuing...', error);
        syncErrors.push(error);
      }
    }

    // If a cloud item is not in the local items, add it to the local items
    for (const cloudItem of cloudItems) {
      try {
        if (!localItems.find(localItem => localItem.uuid === cloudItem.uuid || localItem.localId === cloudItem.localId)) {
          localItems.push(parseCloudItemFn(cloudItem));
        }
      } catch (error) {
        console.error('Error adding cloud item to local items, continuing...', error);
        syncErrors.push(error);
      }
    }

    // Perform the operations
    console.log(`itemsToCreate[${itemsToCreate.length}], itemsToDelete[${itemsToDelete.length}], itemsToUpdate[${itemsToUpdate.length}]`);
    for (const item of itemsToCreate) {
      try {
        const createdItem = await cloudCrud.create(item);
        updateLocalFromCloud(item, parseCloudItemFn(createdItem, item));
        item.localLastUpdate = null;
      } catch (error) {
        console.error('Error creating item, continuing...', error);
        syncErrors.push(error);
      }
    }
    for (const item of itemsToDelete) {
      try {
        await cloudCrud.delete(item);
        localItems = localItems.filter(localItem => localItem.uuid !== item.uuid);
      } catch (error) {
        console.error('Error deleting item, continuing...', error);
        syncErrors.push(error);
      }
    }
    for (const item of itemsToUpdate) {
      try {
        const updatedItem = await cloudCrud.update(item);
        updateLocalFromCloud(item, parseCloudItemFn(updatedItem, item));
        item.localLastUpdate = null;
      } catch (error) {
        console.error('Error updating item, continuing...', error);
        syncErrors.push(error);
      }
    }
    if (syncErrors.length > 0) {
      console.error(`Sync errors[${syncErrors.length}]`, syncErrors);
    }
    return localItems;
  }

}
